<div class="flex flex-col gap-6">
  <!-- Billing Email Section -->
  @if (savedBillingEmail !== null) {
  <p-card>
    <ng-template pTemplate="header">
      <div class="px-4 pt-4">
        <h3 class="text-lg font-semibold m-0">Rechnungs-E-Mail</h3>
        <p class="text-sm text-surface-500 dark:text-surface-400 mt-1">Rechnungen und Zahlungshinweise gehen an diese Adresse</p>
      </div>
    </ng-template>

    @if (isAdmin$ | async) {
      <form class="flex flex-col sm:flex-row gap-3" (ngSubmit)="saveBillingEmail()">
        <label for="billing-email" class="sr-only">Rechnungs-E-Mail</label>
        <input pInputText id="billing-email" name="billingEmail" type="email" autocomplete="email" class="flex-1"
               [(ngModel)]="billingEmail" [attr.aria-invalid]="!isBillingEmailValid()"/>
        <p-button type="submit" label="Speichern" [rounded]="true" [loading]="isSavingBillingEmail"
                  [disabled]="!isBillingEmailValid() || billingEmail.trim() === savedBillingEmail"/>
      </form>
    } @else {
      <span class="font-medium">{{ savedBillingEmail }}</span>
    }
  </p-card>
  }

  <!-- Payment Methods Section -->
  <p-card>
    <ng-template pTemplate="header">
      <div class="flex justify-between items-center px-4 pt-4">
        <div>
          <h3 class="text-lg font-semibold m-0">Zahlungsmethoden</h3>
          <p class="text-sm text-surface-500 dark:text-surface-400 mt-1">Verwalte deine gespeicherten Zahlungsmethoden</p>
        </div>
        @if (isAdmin$ | async) {
        <p-button
          icon="pi pi-plus"
          label="Hinzufügen"
          [rounded]="true"
          (onClick)="addPaymentMethod()"/>
        }
      </div>
    </ng-template>

    <div class="flex flex-col gap-3">
      @for (method of paymentMethods; track method.stripePaymentMethodId) {
        <div class="flex items-center justify-between p-4 border border-surface-200 dark:border-surface-700 rounded-lg">
          <div class="flex items-center gap-4">
            <div class="w-12 h-8 bg-surface-100 dark:bg-surface-800 rounded flex items-center justify-center">
              @if (method.type === 'card') {
                <zaeper-icon fontSet="material-icons-outlined">credit_card</zaeper-icon>
              } @else {
                <zaeper-icon fontSet="material-icons-outlined">account_balance</zaeper-icon>
              }
            </div>
            <div>
              <div class="flex items-center gap-2">
                <span class="font-medium">{{ method.cardBrand }} •••• {{ method.cardLastFour }}</span>
                @if (method.isDefault) {
                  <p-tag value="Standard"/>
                }
              </div>
              <span class="text-sm text-surface-500 dark:text-surface-400">Gültig bis {{ method.cardExpMonth }}/{{ method.cardExpYear }}</span>
            </div>
          </div>
          <div class="flex gap-2">
            @if (!method.isDefault) {
              @if (isAdmin$ | async) {
              <p-button
                icon="pi pi-check"
                [outlined]="true"
                [rounded]="true"
                pTooltip="Als Standard setzen"
                (onClick)="setDefaultPaymentMethod(method)"/>
              }
            }
            @if (isAdmin$ | async) {
            <p-button
              icon="pi pi-trash"
              [outlined]="true"
              [rounded]="true"
              severity="danger"
              pTooltip="Entfernen"
              (onClick)="deletePaymentMethod(method)"/>
            }
          </div>
        </div>
      } @empty {
        @if (!isLoading) {
          <div class="text-center py-8 text-surface-500 dark:text-surface-400">
            <zaeper-icon fontSet="material-icons-outlined" class="text-4xl mb-2">credit_card_off</zaeper-icon>
            <p>Keine Zahlungsmethoden hinterlegt</p>
            <p class="text-sm">Füge eine Zahlungsmethode hinzu, um Abonnements zu verwalten</p>
          </div>
        }
      }
    </div>
  </p-card>

  <!-- Current Plan Section -->
  <p-card>
    <ng-template pTemplate="header">
      <div class="px-4 pt-4">
        <h3 class="text-lg font-semibold m-0">Aktuelles Abonnement</h3>
        <p class="text-sm text-surface-500 dark:text-surface-400 mt-1">Dein aktueller Tarif und Abrechnungszeitraum</p>
      </div>
    </ng-template>

    @if (getActiveSubscription(); as subscription) {
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 bg-surface-50 dark:bg-surface-800 rounded-lg">
        <div>
          <div class="flex items-center gap-2">
            <span class="text-xl font-semibold">{{ subscription.billingInterval === 'MONTHLY' ? 'Monatlich' : 'Jährlich' }}</span>
            @if (subscription.cancelAtPeriodEnd) {
              <p-tag value="Wird gekündigt" severity="warn"/>
            } @else {
              <p-tag [value]="subscription.status === 'ACTIVE' ? 'Aktiv' : subscription.status"/>
            }
          </div>
          @if (subscription.cancelAtPeriodEnd) {
            <p class="text-sm text-orange-600 dark:text-orange-400 mt-1">
              <i class="pi pi-info-circle mr-1"></i>
              Wird am {{ formatDate(subscription.currentPeriodEnd) }} gekündigt. Bis dahin bleibt dein Abonnement aktiv.
            </p>
          } @else {
            <p class="text-sm text-surface-400 dark:text-surface-500 mt-1">Nächste Abrechnung: {{ formatDate(subscription.currentPeriodEnd) }}</p>
          }
        </div>
        <div>
          @if (!subscription.cancelAtPeriodEnd) {
            @if (isAdmin$ | async) {
            <p-button
              label="Kündigen"
              severity="danger"
              [outlined]="true"
              [rounded]="true"
              [loading]="isCancelling"
              icon="pi pi-times"
              (onClick)="cancelSubscription()"/>
            }
          }
        </div>
      </div>

      @if (subscription.items.length > 0) {
        <div class="flex flex-col gap-2 mt-4">
          @for (item of subscription.items; track item.stripeSubscriptionItemId) {
            <div class="flex justify-between items-center px-4 py-2 border border-surface-200 dark:border-surface-700 rounded">
              <span class="text-sm">{{ item.productKey }}</span>
              <span class="text-sm text-surface-500 dark:text-surface-400">{{ item.quantity }}x</span>
            </div>
          }
        </div>
      }
    } @else {
      @if (!isLoading) {
        <div class="text-center py-8 text-surface-500 dark:text-surface-400">
          <zaeper-icon fontSet="material-icons-outlined" class="text-4xl mb-2">shopping_cart</zaeper-icon>
          <p>Kein aktives Abonnement</p>
          <p class="text-sm">Du nutzt derzeit den kostenlosen Tarif</p>
        </div>
      }
    }
  </p-card>
</div>
